import { BitkitAvatar, BitkitAvatarProps, BitkitIconComponent } from '@bitrise/bitkit-v2';

import { getProjectTypeIcon, ProjectType } from '@/models/ProjectType';

// The props AppAvatar owns — looser/narrower than BitkitAvatar's (optional name, nullable src,
// app-only sizes). Declared once; also drives what the passthrough Omit strips below.
type AppAvatarContent = {
  name?: string;
  src?: string | null;
  size?: '40' | '48';
};

// BitkitAvatar's image+icon member minus the props AppAvatar controls (variant/icon set internally,
// content props re-declared above), leaving just layout props (margin, gridRow, aria-*). The badge
// is at most one of projectType (→ platform icon) or icon — both optional, so "no badge" is valid.
type AppAvatarProps = Omit<
  Extract<BitkitAvatarProps, { variant: 'image+icon' }>,
  'variant' | 'icon' | keyof AppAvatarContent
> &
  AppAvatarContent &
  ({ projectType?: ProjectType | null; icon?: never } | { icon?: BitkitIconComponent; projectType?: never });

// App avatar (image, falling back to initials). Badges with the platform/brand icon when a
// projectType or icon is given; otherwise a plain avatar (no badge).
const AppAvatar = ({ name = '', src, size = '48', projectType, icon, ...rest }: AppAvatarProps) => {
  const badge = icon ?? (projectType ? getProjectTypeIcon(projectType) : undefined);
  return badge ? (
    <BitkitAvatar variant="image+icon" size={size} name={name} src={src || ''} icon={badge} {...rest} />
  ) : (
    <BitkitAvatar variant="image" size={size} name={name} src={src || ''} {...rest} />
  );
};

export default AppAvatar;
